import { useState, useCallback } from "react"
import Popup from "reactjs-popup"
import { FaExclamationCircle } from "react-icons/fa"
import { useSelector } from "react-redux"

import { useAuth0 } from "@auth0/auth0-react"
import DropdownCombobox from "../FormElements/DropdownCombobox"
import {
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow,
	getCurrentTime,
	timeStringToMinutes,
	convertMinutesToMilitaryTime,
	getClinicTimes
} from "../../../../utils/generalUtils"
import { scheduleSelect } from "../scheduleSelection"
import useFetchSettings from "../../../../hooks/useFetchSettings"

/*
type NurseSelection = {
  nurseId: string
  startTime: number
  endTime: number
}

type scheduleSelection = "today" | "tomorrow" | "sandbox"
*/

//Time format is HH:MM in 24h format
//returns true if time is not in the format HH:MM
const checkTimeFormat = time => !time || !/^\d{2}:\d{2}$/.test(time)

const DEFAULT_END_TIME = "20:00"

const PickNursePopup = ({ isOpen, onClose, onNurseSelected, scheduleSelection, logging }) => {
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)

	const currentTime = getCurrentTime(sandbox, isTomorrowMode)

	const [nurseSelection, setNurseSelection] = useState({
		nurseId: "",
		startTime: currentTime,
		endTime: timeStringToMinutes(DEFAULT_END_TIME)
	})
	const [validationErrors, setValidationErrors] = useState({
		nurseName: "",
		nurseStartTime: "",
		nurseEndTime: ""
	})

	const { user } = useAuth0()
	const { settingsData: settings } = useFetchSettings()

	const todaysNurses = useSelector(state => state.schedule.nurseSchedule)
	const sandboxNurses = useSelector(state => state.sandbox.nurseSchedule)
	const tomorrowNurses = useSelector(state => state.tomorrow.nurseSchedule)

	const nursesToUse = scheduleSelect({
		scheduleSelection,
		todayOption: todaysNurses,
		tomorrowOption: tomorrowNurses,
		sandboxOption: sandboxNurses
	}).map(nurse => nurse.nurseId) // Get the nurse IDs

	const centerOpen = settings?.centerOpen
	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerClosedDates = settings?.center_closed_dates || []

	// Get the appropriate date based on schedule selection
	const targetDate =
		scheduleSelection === "tomorrow"
			? getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
			: getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)

	const { openTime, closeTime } = getClinicTimes(settings, targetDate)

	const validateNurseSelection = useCallback(
		nurseSelection => {
			const currentMinutes = currentTime
			const newErrors = {
				nurseName: "",
				nurseStartTime: "",
				nurseEndTime: ""
			}

			let isValid = true

			if (!nurseSelection.nurseId) {
				newErrors.nurseName = "Must select a nurse to add."
				isValid = false
			}

			const { startTime, endTime } = nurseSelection
			if (!startTime) {
				newErrors.nurseStartTime = 'Must be in the format "HH:MM" and cannot be empty.'
				isValid = false
			}
			if (!endTime) {
				newErrors.nurseEndTime = 'Must be in the format "HH:MM" and cannot be empty.'
				isValid = false
			}
			if (startTime >= endTime) {
				newErrors.nurseStartTime = "Start Time must be before End Time."
				newErrors.nurseEndTime = "End Time must be after Start Time."
				isValid = false
			}
			if (startTime < openTime - 60) {
				newErrors.nurseStartTime = "Start Time must be at or after the center open time."
				isValid = false
			}
			if (startTime <= currentMinutes - 6) {
				newErrors.nurseStartTime = "Start Time must be after the current time."
				isValid = false
			}
			if (endTime > closeTime + 120) {
				newErrors.nurseEndTime = "End Time must be at or before the center close time."
				isValid = false
			}

			setValidationErrors(newErrors)
			return isValid
		},
		// These values are read at callback-construction time and intentionally captured; adding them would defeat memoization.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[settings]
	)

	const settingsNurses = Array.isArray(settings.nurses)
		? settings.nurses
		: Object.values(settings.nurses)

	const availableNurses = settingsNurses.filter(nurse => !nursesToUse.includes(nurse.nurseId))

	return (
		<Popup
			modal
			nested
			contentStyle={{
				width: "auto",
				border: "none",
				borderRadius: "10px",
				padding: "0"
			}}
			overlayStyle={{ background: "rgba(0, 0, 0, 0.5)" }}
			open={isOpen}
			onClose={onClose}
		>
			<div className="bg-white p-6 rounded-xl shadow-md w-96 mx-auto" tabIndex="0">
				<div className="text-xl font-semibold mb-4 text-custom-blue">Add Nurse</div>
				<div className="mb-4">
					<label className="block text-sm font-medium text-gray-700">Name</label>
					<div className="relative mt-2 rounded-md shadow-sm">
						<DropdownCombobox
							label="Enter Nurse Assigned..."
							options={availableNurses}
							displayKey={nurse => `${nurse.nurseName}`}
							name="nurseName"
							value={nurseSelection.nurseId}
							onChange={value => {
								setNurseSelection(nurseSelection => ({
									...nurseSelection,
									nurseId: value
								}))
								logging.info("AddNursePopup - Nurse selected", {
									component: "AddNursePopup",
									function: "onChange",
									nurse: availableNurses.find(nurse => nurse.nurseId === value)
								})
								if (value) {
									setValidationErrors(prevErrors => ({
										...prevErrors,
										nurseName: ""
									}))
								}
							}}
							valueKey="nurseId"
						/>

						{validationErrors.nurseName && (
							<div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
								<FaExclamationCircle
									aria-hidden="true"
									className="h-5 w-5 text-red-500"
								/>
							</div>
						)}
					</div>
					{validationErrors.nurseName && (
						<p id="nurseName-error" className="mt-2 text-sm text-red-600">
							{validationErrors.nurseName}
						</p>
					)}
				</div>
				<div className="mb-4 grid grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-700">
							Start Time
						</label>
						<div className="relative mt-2 rounded-md shadow-sm">
							<input
								type="time"
								className={`block w-full rounded-md border-0 py-1.5 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
									validationErrors.nurseStartTime
										? "pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
										: "ring-1 ring-inset ring-gray-300 focus:ring-custom-blue"
								}`}
								name="nurseStartTime"
								defaultValue={convertMinutesToMilitaryTime(currentTime)}
								onChange={e => {
									const value = e.target.value
									logging.info("AddNursePopup - Nurse start time changed", {
										component: "AddNursePopup",
										function: "onChange",
										value: value
									})
									if (checkTimeFormat(value)) {
										setValidationErrors(prevErrors => ({
											...prevErrors,
											nurseStartTime: `Must be in the format "HH:MM" and cannot be empty.`
										}))
										setNurseSelection(nurseSelection => ({
											...nurseSelection,
											startTime: null
										}))
									} else {
										setValidationErrors(prevErrors => ({
											...prevErrors,
											nurseStartTime: ""
										}))
										setNurseSelection(nurseSelection => ({
											...nurseSelection,
											startTime: timeStringToMinutes(value)
										}))
									}
								}}
								aria-invalid={validationErrors.nurseStartTime ? "true" : "false"}
								aria-describedby={
									validationErrors.nurseStartTime
										? "nurseStartTime-error"
										: undefined
								}
							/>
							{validationErrors.nurseStartTime && (
								<div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
									<FaExclamationCircle
										aria-hidden="true"
										className="h-5 w-5 text-red-500"
									/>
								</div>
							)}
						</div>
						{validationErrors.nurseStartTime && (
							<p id="nurseStartTime-error" className="mt-2 text-sm text-red-600">
								{validationErrors.nurseStartTime}
							</p>
						)}
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700">End Time</label>
						<div className="relative mt-2 rounded-md shadow-sm">
							<input
								type="time"
								className={`block w-full rounded-md border-0 py-1.5 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
									validationErrors.nurseEndTime
										? "pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
										: "ring-1 ring-inset ring-gray-300 focus:ring-custom-blue"
								}`}
								name="nurseEndTime"
								defaultValue={DEFAULT_END_TIME}
								onChange={e => {
									const value = e.target.value
									logging.info("AddNursePopup - Nurse end time changed", {
										component: "AddNursePopup",
										function: "onChange",
										value: value
									})
									if (checkTimeFormat(value)) {
										setValidationErrors(prevErrors => ({
											...prevErrors,
											nurseEndTime: `Must be in the format "HH:MM" and cannot be empty.`
										}))
										setNurseSelection(nurseSelection => ({
											...nurseSelection,
											endTime: null
										}))
									} else {
										setValidationErrors(prevErrors => ({
											...prevErrors,
											nurseEndTime: ""
										}))
										setNurseSelection(nurseSelection => ({
											...nurseSelection,
											endTime: timeStringToMinutes(value)
										}))
									}
								}}
								aria-invalid={validationErrors.nurseEndTime ? "true" : "false"}
								aria-describedby={
									validationErrors.nurseEndTime ? "nurseEndTime-error" : undefined
								}
							/>
							{validationErrors.nurseEndTime && (
								<div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
									<FaExclamationCircle
										aria-hidden="true"
										className="h-5 w-5 text-red-500"
									/>
								</div>
							)}
						</div>
						{validationErrors.nurseEndTime && (
							<p id="nurseEndTime-error" className="mt-2 text-sm text-red-600">
								{validationErrors.nurseEndTime}
							</p>
						)}
					</div>
				</div>
				<div className="flex justify-end">
					<button
						onClick={async () => {
							const selectionValid = validateNurseSelection(nurseSelection)
							if (selectionValid) {
								onNurseSelected(nurseSelection)
							}
						}}
						className="bg-custom-blue text-white text-sm font-semibold px-4 py-2 rounded"
					>
						Add
					</button>
				</div>
			</div>
		</Popup>
	)
}

export default PickNursePopup
